import GameManager from '../../cvp-webgames-core/GameManager';
import BaseScenarioPart from '../../cvp-webgames-core/Scenario/BaseScenarioPart';
import { DaarcBaseScenario } from '../DaarcBaseScenario';
import { constants } from '../../misc/constants';
import { DebriefIntro, DebriefVortex } from '../../components/ui/DebriefUI';
import { AssessAirwayActionData, AssessAirwayActions } from '../../components/ui/AssessAirwayUI';
import { VortexUICurrentAttempt } from '../../components/ui/hud/VortexUI';
import BaseTestScenario from '../base-test-scenario/BaseTestScenario';

export default class DarnellKennedy extends BaseTestScenario {

	public isUpDown: boolean;
	public isUpDownString: any;
	public isHumidityString: string;
	public isVideoScopeString: any;
	public isHumidity2String: string;

	public isDL: boolean;
	public isVL: boolean;	

	public get maxScore(): number {
		return 54600;
	}

	public get predeterminedPoints(): number {
		var assessPatientRemoveDenturesPoints = 500;
		var setupFiberOpticsPoints = 1000;
		var preCricPoints = 3900;
		var awardedPoints = 32700;

		return assessPatientRemoveDenturesPoints + awardedPoints +
			setupFiberOpticsPoints + preCricPoints;
	}

	public get totalNumberOfQuestions(): number {
		return 5;
	}

	public get currentLevel(): number {
		return 7;
	}

	public getDefaultApneaTime() {
		return 210
	}

	public getDefaultSa02() {
		return 95
	}

	constructor(gameManager: GameManager, scenarioName: string) {
		super(gameManager, scenarioName);

		this._setupIntro();
		this._setupGeneralNeutralState();
		this._setupProcedures();
	}

	public getDebriefIntro(): DebriefIntro {
		return {
			items: [
				"The patient’s esophageal reflux, which is caused by the increased inter-abdominal pressure from the ascites, makes bag valve mask difficult and ultimately unsuccessful.",
                "Because the patient will need to be on mechanical ventilation in the long-term, the patient needs to be intubated. It is acceptable to try other rescue techniques in order to temporize the situation and get back to the “green zone,” but the best approach would be to try intubation first. If you do have a fiberoptic available, you could attempt other rescue techniques first, then intubate when the fiberoptic arrives.",
                "Intubation is successful if a video laryngoscope is used. When you use the direct laryngoscope, you will see a grade 4 view (due to neck immobility). The video laryngoscope improves your view to a grade 2 view.",
                "Although any ET tube size works in this scenario, you should have noticed that false vocal cords occluded your view due to airway edema. For this reason, a size 7.0 tube is recommended.",
                "Did you factor in the clinical condition of the patient in your utilization of the Vortex approach?"
			],
			link: 'https://google.com'
		};
	}

	public getDebriefVortex(): DebriefVortex {
		return {
			successItems: [
				'BVM: ' + this.bvmAttemptCount + ' attempt(s)',
				'ETT: ' + this.ettAttemptCount + ' attempt(s)',
				'SGA: ' + this.sgaAttemptCount + ' attempt(s)',
				'Cricothyrotomy: ' + this.cricAttemptCount + ' attempt(s)'
			],
			askYourselfItems: [
				'Did you exceed the maximum number of optimizations (3) for a given attempt?',
				'Did you optimize between each attempt at the bag valve mask, intubation and supraglottic airway?',
				'Did you follow the Vortex approach by not returning to a technique that you had already attempted with appropriate optimizations and failed?',
				'How can you more efficiently manage the airway? Remember that the quicker you establish a stable, patent airway, the better the patient outcome.'
			]
		};
	}

	public onBVMFinished(): void {
		this.showFailedAttemptPopUpAfterAuscultate(VortexUICurrentAttempt.BVM, 'A patent airway has not been achieved. Continue attempts at managing the airway.', 'Failed Attempt');
	}

	public onETTFinished(): void {
		if(this.isVL) {
			this.addScore(this.predeterminedPoints);
			this._CheckOxy();
			this.showSuccessPopUpAfterAuscultate(VortexUICurrentAttempt.ETT, 'You established a stable, patent airway for Mr. Darnell Kennedy by intubating him using a video laryngoscope. Mr. Kennedy was apneic for ' + this.apneaTimeElapsed + ' while you managed his airway. You earned ' + this.totalScore + ' out of a possible ' + this.maxScore + ' points. Review the debrief for a summary of key learning points.');
		} else this.showFailedAttemptPopUpAfterAuscultate(VortexUICurrentAttempt.ETT, 'A patent airway has not been achieved. Continue attempts at managing the airway.', 'Failed Attempt');
		//this.isVL = false
	}

	public onSGAFinished() : void  {
		if(this.isUpDown) {
			this.showTempSuccessPopUpAfterAuscultate('We’re getting air in! But this patient is going to be on a ventilator. We need to try and intubate.', VortexUICurrentAttempt.SGA);
		} else this.showFailedAttemptPopUpAfterAuscultate(VortexUICurrentAttempt.SGA, 'A patent airway has not been achieved. Continue attempts at managing the airway.', 'Failed Attempt');
		//this.isUpDown = false
	}

	public onCricFinished(): void {
		this.cricAttemptCount = 1;
		this._CheckOxy();
		this.showFailedScenarioPopUpAfterAuscultate(VortexUICurrentAttempt.Cric, 'You should not have attempted a cricothyrotomy. A stable, patent airway can be established through non-surgical attempts. Review the debrief and then try again.');
	}

	public _CheckOxy() {
		if(!this.preOxDone) {
			this.debriefLogFailedItem("Pre-Oxygenation: You did not pre-oxygenate the patient.")
		};
		if(!this.callFlexBr) {
			this.debriefLogFailedItem("Call for flexible bronchoscope: Recognizing the need for a flexible bronchoscope early is critical, especially when the patient’s condition indicates intubation and use of a supraglottic airway will be anatomically impossible.")
		}	
	}

	public getAssessAirwayData(): AssessAirwayActionData[] {
		return [
			{
				relatedAction: AssessAirwayActions.ExamineNeck,
				title: 'Examine Neck',
				text: 'Trachea is not deviated to one side or the other. Tissue is mobile. You can identify the cricothyroid membrane with your finger.'
			},
			{
				relatedAction: AssessAirwayActions.LiftChinToCeiling,
				title: 'Lift chin to ceiling',
				text: 'Patient cannot lift head; neck is fused in the neutral position <br /> Thyromental Distance: 4 finger breadths (6 cm) <br /> Hyoid to Thyroid Notch Distance: 2 finger breadths (3 cm)'
			},
			{
				relatedAction: AssessAirwayActions.PutChinToChest,
				title: 'Put chin to chest',
				text: 'Patient cannot do this action; neck is fused in the neutral position'
			},
			{
				relatedAction: AssessAirwayActions.RemoveDentures,
				title: 'Remove Dentures',
				text: 'The patient is not wearing dentures',
				noPoints: true
			},
			{
				relatedAction: AssessAirwayActions.ShowTeeth,
				title: 'Smile for me',
				text: 'Yes'
			},
			{
				relatedAction: AssessAirwayActions.StickOutTongue,
				title: 'Open mouth',
				text: 'Inter-incisor Distance: 3 finger breadths (4 cm)',
				imgUrl:'./assets/images/assess-airway-images/Class4.png'
			}
		]
	}

	private _setupIntro() {
		// this.intro.config.part1Config = {
		// 	videoUrl: './assets/videos/scenario6/daarc-scenario6-darnellkennedy-v1.mp4',
		// 	dialogs: [
		// 		{
		// 			speakerName: constants.speakers.RnMiranda,
		// 			text: `
		// 			This is Darnell Kennedy. He’s a 55-year-old male, 68 inches, 100 kg. 
		// 			He suffers from cirrhosis, cervical fusion, and cachexia. He has a 
		// 			history of prior heavy alcohol use and has massive ascites. He currently 
		// 			has spontaneous bacterial peritonitis and is becoming septic. He has a 
		// 			temperature of 38.5 C. He has a K of 3. He hasn’t eaten this morning.
		// 			`,
		// 			VideoChapter: () =>'DAARC - Scenario6 - intro - 53'
		// 		},
		// 		{
		// 			speakerName: constants.speakers.DrCaleb,
		// 			text: `
		// 			This patient is rapidly deteriorating due to peritonitis and will require intubation for resuscitation and ventilatory support. I’ll manage the
		// 				hemodynamics, you manage the airway.
		// 			`,
		// 			VideoChapter: () =>'DAARC - Scenario6 - intro - 53'
		// 		}
		// 	],
		// 	sbarConfig: {
		// 		s: `He currently has spontaneous bacterial peritonitis and is becoming septic. He has a temperature of 38.5 C.`,
		// 		b: `
		// 			Age: 55<br />
		// 			Height (in): 68 in<br />
		// 			Weight (kg): 100 kg<br />
		// 			Potassium (K): 3.0<br />
		// 			NPO Status:NPO (has not eaten this AM)<br />
		// 			<br />
		// 			PMH: Darnell Kennedy suffers from cirrhosis, cervical fusion, and cachexia. He has a history of prior heavy alcohol use and has massive ascites.
		// 		`,
		// 		a: `Darnell has rapidly evolving sepsis due to peritonitis and will require intubation for resuscitation and ventilatory support`,
		// 		r: `This patient requires intubation for resuscitation and ventilatory support.`
		// 	}
		// }

		this._id_ScenarioSet(6); // <<<<<< CRITICAL: must be set in every scenario. This number does not always match the "level number" variable set in the declarations above, use the documented level numbers and not how they are listed in game <<<<<<<<

		this.intro.config.part1Config = {
			videoUrl: './assets/videos/scenario6/daarc-scenario6-darnellkennedy-v1.mp4',
			chapters: [
				{
					chapterName: () => 'DAARC - Scenario6 - intro - 53'
				}
			],
			sbarConfig: {
				s: `He currently has spontaneous bacterial peritonitis and is becoming septic. He has a temperature of 38.5 C.`,
				b: `
					Age: 55<br />
					Height (in): 68 in<br />
					Weight (kg): 100 kg<br />
					Potassium (K): 3.0<br />
					NPO Status: NPO (has not eaten this AM)<br />
					<br />
					PMH: Darnell Kennedy suffers from cirrhosis, cervical fusion, and cachexia. He has a history of prior heavy alcohol use and has massive ascites.
				`,
				b508: `
					Age. 55.
					Height. 68 inches.
					Weight. 100 kilograms.
					Potassium. 3.0.
					NPO Status NPO has not eaten this A M.
					Past Medical History. Darnell Kennedy suffers from cirrhosis cervical fusion and cachexia. He has a history of prior heavy alcohol use and has massive ascites.
				`,
				a: `Darnell has rapidly evolving sepsis due to peritonitis and will require intubation for resuscitation and ventilatory support`,
				r: `This patient requires intubation for resuscitation and ventilatory support.`
			}
		}
	}

	private _setupGeneralNeutralState() {
		this.neutralState.config.part1Config = {
			videoUrl: './assets/videos/scenario6/daarc-scenario6-darnellkennedy-v1.mp4',
			hudIdleVideoChapter: () =>'DAARC - Scenario6 - Flat - Nothing - 1',
			onCalledForVortex: () => {
				this.debriefLog('Called for a Vortex reader')
			}
		};

		var toolsArr = null
		if(this.isLite) {
			toolsArr = [
				constants.toolbagTools.bvm,
				constants.toolbagTools.ett,
				constants.toolbagTools.sga,	
				//constants.toolbagTools.cric //comment this out on cric not required scenarios		
			]
		} else {			
				toolsArr = [
				constants.toolbagTools.bvm,
				constants.toolbagTools.ett,
				constants.toolbagTools.sga,
				constants.toolbagTools.cric
			]
		}

		this.neutralState.config.part2Config = {
			videoUrl: './assets/videos/scenario6/daarc-scenario6-darnellkennedy-v1.mp4',
			toolsToChooseFrom: toolsArr,
			onTeamNotDebriefed: () => {
				this.debriefLogFailedItem("Brief other staff in the room about attempt order: Briefing your staff on the attempt order will help them anticipate providing the right equipment. Briefings empower your staff to notify you if you fall into fixation error.")
            },
            onTeamDebriefed: () => {
                this.debriefLog('Briefed other staff in the room about attempt order');
            },
            onToolsOrganized: () => {
                this.debriefLog('Organized equipment');
            }
		};

		this.neutralState.config.part3Config = {
			videoUrl: './assets/videos/scenario6/daarc-scenario6-darnellkennedy-v1.mp4',
			waitForSuctionVideoChapter: () =>'DAARC - Scenario6 - Flat - Nothing - 1',
			onSuctionSet: () => {
				this.debriefLog("Suction Set") 
			}
		};

		this.neutralState.config.part4Config = {
			videoUrl: './assets/videos/scenario6/daarc-scenario6-darnellkennedy-v1.mp4',
			waitForAdjustBedHeightVideoChapter: () =>'DAARC - Scenario6 - 45 - Nothing - 7',
			onAdjustedBedHeight: () => {
				this._id_bedPos = this._id_bedAdj; // <<<<<<<
				this.debriefLog("Bed Adjusted");
			}
		};

		this.neutralState.config.part5Config = {
			videoUrl: './assets/videos/scenario6/daarc-scenario6-darnellkennedy-v1.mp4',
			liftChinVideoChapter: () =>'DAARC - Scenario6 - 45 - patient-assessment-straight - 35',
			chinToChestVideoChapter: () =>'DAARC - Scenario6 - 45 - patient-assessment-straight - 35',
			examineNeckVideoChapter: () =>'DAARC - Scenario6 - 45 - patient-assessment-straight - 35',
			stickOutTongueVideoChapter: () =>'DAARC - Scenario6 - 45 - patient-assessment-straight - 35',
			realTeethVideoChapter: () =>'DAARC - Scenario6 - 45 - patient-assessment-straight - 35',
			onAssessmentFinied: () => {
				this.debriefLog("Performed a complete airway assessment")
			}
		};

		this.neutralState.config.part6Config = {
			videoUrl: './assets/videos/scenario6/daarc-scenario6-darnellkennedy-v1.mp4',
			waitForFiberOpticVideoChapter: () => this._id_SegmentAssembly(this._id_preOxText),
			onSetupFiberOptic: () => {
				//this.isDL = true;
				this.callFlexBr = true;
				this.debriefLog("Called for advanced airway cart"); // <<<<<<<<<<<<<
				/*if(this.isDL) {
					this.isVideoScopeString = this.hoverBreathYes;
					this.isHumidity2String = this.auscSuccess;
				} else {
					this.isVideoScopeString = this.hoverBreathNo;
					this.isHumidity2String = this.auscFailed;
				};*/
			},
			notificationText: 'The VL/FBS is available for use.'
		};

		this.neutralState.config.part7Config = {
			videoUrl: './assets/videos/scenario6/daarc-scenario6-darnellkennedy-v1.mp4',
			waitForPreOxygenateVideoChapter: () => this._id_SegmentAssembly(this._id_preOxText), // this needs to update based on the selection made		
			onPreOxygenated: (selection: number) => {
				this.preOxDone = true;
				this._id_UpdatePreOxText(selection); // this should update the pre-oxygenation neutral video segment, but the reference set above might not reflect this update
				switch(selection) {
					case 0:
						this.debriefLog("Pre-Oxygenated with High Flow Nasal Cannula");
						break;
					case 1:
						this.debriefLog("Pre-Oxygenated with Nasal Noninvasive Positive Pressure");
						break;
					case 2:
						this.debriefLog("Pre-Oxygenated with 100% Non-Rebreather Mask");
						break;
					case 3:
						this.debriefLogFailedItem("Pre-Oxygenated with BVM. This may not be the best solution");
						break;
				}
			}
		};

		this.neutralState.config.part8Config = {
			onApneaStartedVideoChapter: () => this._id_SegmentAssembly(this._id_preOxText), // APNEA TIMER INPUT SEGMENT CHANGE HERE,
			videoUrl: './assets/videos/scenario6/daarc-scenario6-darnellkennedy-v1.mp4',
			meds: [
				{
					title: 'Induction Agent',
					audioPrefix: 'InductionAgent_'
				},
				{
					title: 'Administer Neuromuscular Junction Blocker',
					audioPrefix: 'NMJB_'
				}
			],
			onPatientFullyMedicated: () => {	
				this.debriefSetAdministeringText("You correctly medicated your patient and earned maximum points.")
			},
			onSingleMedAdministered: () => {
				this.debriefSetAdministeringText("Did you administer all medications that are appropriate, given the patient's condition?") //May need more description			
			}
		};
	}

	private _setupProcedures() {
		this._setupBvm();
		this._setupEtt();
		this._setupSga();
		this._setupCric();
		this._setupAuscultate();
	}

	private _setupBvm() {
		this.procedures.bvm.config.part1Config = {
			videoUrl: './assets/videos/scenario6/daarc-scenario6-darnellkennedy-v1.mp4',
			chooseHandVideoChapter: () => this._id_bvmHandAttempt,
			onChooseHandNextClicked: (isOneHanded: boolean) => {
				if(isOneHanded) {
					this._id_bvmHandAttempt = this._id_SegmentAssembly(this._id_BVMAttempt(1)) // set segment variable equal to this
				} else {
					this._id_bvmHandAttempt = this._id_SegmentAssembly(this._id_BVMAttempt(2)) // set segment variable equal to this
				}
			}
		};
		this.procedures.bvm.config.part2Config = {
			videoUrl: './assets/videos/scenario6/daarc-scenario6-darnellkennedy-v1.mp4',
			onProcedureAttempted: (attemptCount: number) => {
				this.bvmAttemptCount = attemptCount;
			}
		}
	}

	private _setupEtt() {
		this.procedures.ett.config.isAttemptSuccessful = () => {
			return this.isVL;
		}		

		this.procedures.ett.config.part1Config = {			
			hideVlButton: () => !this.callFlexBr, // If Neutral step 6 has not been performed, the VL button should be hidden
			videoUrl: './assets/videos/scenario6/daarc-scenario6-darnellkennedy-v1.mp4',
			squeezeAmbuBagVideoChapter: () => this._id_SegmentAssembly(this._id_ETTAttempt()),			
			insertingETTVideoChapter: () => this._id_SegmentAssembly(this._id_ETTAttempt()),			
			ettSpecialUIConfig: {
				airwayImage: () => {
					if(this.isDL) {
						return './assets/images/ett/Grade4.png';
					} else if (this.isVL) { 
						return './assets/images/ett/Grade2.png';
					} 
					else {
						return './assets/images/ett/Grade4png';
					}
				},
				magillForceps: {
					text: () => 'There is no foreign body in the patient’s airway.'
				},
				suction: {
					text: () => ''
				},
				manipulateLarynx: {
					text: () => ''
				}
			},
			onProcedureAttempted: (attemptCount: number) => {
				this.ettAttemptCount = attemptCount;
			},
			onMakeAttemptClicked: (isDLClicked: boolean, isVLClicked: boolean) => {
				//this.isDL = !isDLClicked;
				if(isDLClicked){
					this.isDL = true;
					this.isVL = false;
				} else if(isVLClicked) { // VL shouldbe selected for the answer to be correct
					this.isDL = false;
					this.isVL = true;
				} else { // it should not be possible to select neither, just a fail safe
					this.isDL = false;
					this.isVL = false;
				}
			}
		};
	}

	private _setupSga() {
		this.procedures.sga.config.isTempSuccess = () => {
			return this.isUpDown;
		}

		this.procedures.sga.config.onUpDownManeuverClickedInOptimizations = () => {
			this.isUpDown = true;
		/*if(this.isUpDown) {
				this.isUpDownString = this.hoverBreathYes;
				this.isHumidityString = this.auscSuccess;
			} else {
				this.isUpDownString = this.hoverBreathNo;
				this.isHumidityString = this.auscFailed;
			}; */
		};

		this.procedures.sga.config.part1Config = {
			videoUrl: './assets/videos/scenario6/daarc-scenario6-darnellkennedy-v1.mp4',
			squeezeAmbuBagVideoChapter: () =>this._id_SegmentAssembly(this._id_SGAAttempt()),
			insertingSgaVideoChapter: () =>this._id_SegmentAssembly(this._id_SGAAttempt()),
			onProcedureAttempted: (attemptCount: number) => {
				this.sgaAttemptCount = attemptCount;
			}
		}
	}

	private _setupCric() {
		this.procedures.cric.config.isAttemptSuccessful = () => {
			return true;
		};
		
		this.procedures.cric.config.part1Config = {
			videoUrl: './assets/videos/scenario6/daarc-scenario6-darnellkennedy-v1.mp4',
			correctToolOrder: [
				'Scalpel',
				'Bougie',
				'ETT',
				'Spreader',
				'Syringe',
			]
		};
		this.procedures.cric.config.part2Config = {
			videoUrl: './assets/videos/scenario6/daarc-scenario6-darnellkennedy-v1.mp4',
			identifyLandmarksVideoChapter: () => this._id_SegmentAssembly(this._id_CricPart(1), 'flat'),
		};
		this.procedures.cric.config.part3Config = {
			videoUrl: './assets/videos/scenario6/daarc-scenario6-darnellkennedy-v1.mp4'
		};
		this.procedures.cric.config.part4Config = {
			videoUrl: './assets/videos/scenario6/daarc-scenario6-darnellkennedy-v1.mp4',
			scalpelVideoChapter: () => this._id_SegmentAssembly(this._id_CricPart(2), 'flat')
		};
		this.procedures.cric.config.part5Config = {
			videoUrl: './assets/videos/scenario6/daarc-scenario6-darnellkennedy-v1.mp4',
			spreaderVideoChapter: () => this._id_SegmentAssembly(this._id_CricPart(3), 'flat')
		};
		this.procedures.cric.config.part6Config = {
			videoUrl: './assets/videos/scenario6/daarc-scenario6-darnellkennedy-v1.mp4',
			bougieVideoChapter: () => this._id_SegmentAssembly(this._id_CricPart(4), 'flat')
		};
		this.procedures.cric.config.part7Config = {
			videoUrl: './assets/videos/scenario6/daarc-scenario6-darnellkennedy-v1.mp4',
			etTubeVideoChapter: () => this._id_SegmentAssembly(this._id_CricPart(5), 'flat')
		};
		this.procedures.cric.config.part8Config = {
			videoUrl: './assets/videos/scenario6/daarc-scenario6-darnellkennedy-v1.mp4',
			squeezeAmbuBagVideoChapter: () => this._id_SegmentAssembly(this._id_CricPart(6), 'Flat')
		};
	}

	public setTextAusc() {
		if(this.isVL) {
			return this.auscSuccess;
		}else if(this.isUpDown) {
			return this.auscSuccess;
		} else {
			return this.auscFailed;
		}
	}
	
	public setTextHover() {
		if(this.isVL) {
			return this.hoverBreathYes;
		} else if(this.isUpDown) {
			return this.hoverBreathYes;
		} else {
			return this.hoverBreathGurgle;
		}
	}

	private _setupAuscultate() {		
		this.procedures.auscultate.config.part1Config = {
			videoUrl: './assets/videos/scenario6/daarc-scenario6-darnellkennedy-v1.mp4',
			bvmConfig: {
				moveToAuscultateScreenVideoChapter: () => this._id_SegmentAssembly(this._id_bvmAusc),
				checkRiseAndTubeHumidificationText: () => this.auscFailed,
				audioPath: () => {
					return './assets/audio/auscultate/vortexreaderreminder_fail.mp3';
				},
				isAnswerCorrect: (isYesClicked: boolean) => {
					return !isYesClicked;
				},
				auscultateHoverOverMessages: () => {
					return this.hoverBreathGurgle
				}
			},
			ettConfig: {				
				moveToAuscultateScreenVideoChapter: () => this._id_SegmentAssembly(this._id_ettAusc),
				checkRiseAndTubeHumidificationText: () => this.setTextAusc(),
				audioPath: () => {
					if(this.isVL) {
						return './assets/audio/auscultate/vortexreaderreminder_success.mp3';
					} else return './assets/audio/auscultate/vortexreaderreminder_fail.mp3';
				},
				isAnswerCorrect: (isYesClicked: boolean) => {
					if(this.isVL) {
						return isYesClicked;
					} else return !isYesClicked;
				},
				auscultateHoverOverMessages: () => this.setTextHover()
			},
			sgaConfig: {
				moveToAuscultateScreenVideoChapter: () => this._id_SegmentAssembly(this._id_sgaAusc),
				checkRiseAndTubeHumidificationText: () => this.setTextAusc(),
				audioPath: () => {
					if(this.isUpDown) {
						return './assets/audio/auscultate/vortexreaderreminder_success2.mp3';
					} else return './assets/audio/auscultate/vortexreaderreminder_fail.mp3';
				},
				isAnswerCorrect: (isYesClicked: boolean) => {
					if(this.isUpDown) {
						if(isYesClicked){
							this.isUpDown = false // should only toggle if they select yes
						}
						return isYesClicked;
					} else return !isYesClicked;
				},
				auscultateHoverOverMessages: () => this.setTextHover()				
			},
			cricConfig: {
				moveToAuscultateScreenVideoChapter: () => this._id_SegmentAssembly(this._id_cricAusc, 'flat'),
				checkRiseAndTubeHumidificationText: () => this.auscSuccess,
				audioPath: () => {
					return './assets/audio/auscultate/vortexreaderreminder_success2.mp3';
				},
				isAnswerCorrect: (isYesClicked: boolean) => {
					return isYesClicked;
				},
				auscultateHoverOverMessages: () => {
					return this.hoverBreathYes
				}
			}
		}
	}


	public getParts(): BaseScenarioPart<DaarcBaseScenario>[] {
		return this.parts;
	}

	public getSoundPath(fileName: string): string {
		return './assets/audio/scenario1/' + fileName + '.ogg';
	}

	public showDefaultSideMenu() {
		const context = this;
		context.sideMenuId = context.uiWrapper.displaySideMenu({
			gameManager: this.gameManager,
			inductionTimeInSeconds: 30,
			apneaTimeInSeconds: 210,
			bloodPressureNumerator: 80,
			bloodPressureDenominator: 50,
			bloodPressureMinNumerator: 50,
			bloodPressureMinDenominator: 33,
			bloodPressureMaxNumerator: 220,
			bloodPressureMaxDenominator: 110,
			sa02: 95,
			heartRate: 150,
			heartRateMax: 200,
			respirationRate: 22,
			resetEtc02: false,
			etc02: 35,
			showETC02: false,
			isApneaTimerIdle: true,
			onInductionTimerRanOut: () => {
				context.hasInductionTimerRanOut = true;
			},
			onApneaTimerRanOut: () => {
                context.failedScenario = true;
		this._CheckOxy();
				context.onShowSuccessFailedAttempt(constants.attemptText.failedTitle, 'You did not establish a stable, patent airway in time for Darnell Kennedy. He has expired. Review the debrief and then try again.', true);
			}
		}, context.sideMenuId);
	}
}